import { FloatingOrderPanel } from '@/components/sale/FloatingOrderPanel';
import { useChat } from '@/contexts/ChatContext';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { getPageId } from '@/services/utl-service';
import { useCallback, useState } from 'react';
import { SaleFloatingWidgets } from './sale-floating-widgets';
import { SaleSidebarColumn } from './sale-sidebar-column';
import { SaleTabKey } from './sale-sidebar-tabs';
import { useFloatingWidgets } from '../hooks/use-floating-widgets';
import { useFloatingOrder } from '../hooks/use-floating-order';

/** Sidebar + live comments/chat floats — isolated so product grid doesn't re-render on every comment. */
export function SaleChannelColumn() {
  const [activeChannelTab, setActiveChannelTab] = useState<SaleTabKey>('live');

  const {
    showingComment,
    setShowingComment,
    showingChatMessage,
    setShowingChatMessage,
    comments,
    showingCommentsFloat,
    showingMessagingFloat,
    fetchCommentState,
    handleSelectPost,
    handleTabChange: handleWidgetTabChange,
    handleConversationSelect,
    handleSelectQueueCustomer,
    isPolling,
    loadMoreComment,
    autoDetectChat,
    autoDetectComment,
    handleUpdateAutoDetectChat,
    handleUpdateAutoDetectComment,
  } = useFloatingWidgets();

  const {
    floatingCustomer,
    floatingOpen,
    floatingInitialData,
    handleProductDropOnQueue,
    handleConfirmFloatingOrder,
    handleCloseFloating,
  } = useFloatingOrder();

  const { conversations, loadingConversations, refetchConversation, handleGlobalIncomingMessage } =
    useChat();

  const handleChannelTabChange = useCallback(
    (tabKey: SaleTabKey) => {
      setActiveChannelTab(tabKey);
      handleWidgetTabChange(tabKey);
    },
    [handleWidgetTabChange],
  );

  const pageId = getPageId();
  const globalMessageTopic = pageId ? `/topic/conversations/${pageId}` : null;

  const handleChatWsMessage = useCallback(
    (stompMsg: { body: string }) => {
      handleGlobalIncomingMessage(stompMsg.body);
    },
    [handleGlobalIncomingMessage],
  );

  useWebSocketChannel(globalMessageTopic, handleChatWsMessage);

  return (
    <>
      <SaleSidebarColumn
        activeTab={activeChannelTab}
        onTabChange={handleChannelTabChange}
        conversations={conversations}
        loadingConversations={loadingConversations}
        onConversationSelect={handleConversationSelect}
        onPostSelect={handleSelectPost}
        onSelectQueueCustomer={handleSelectQueueCustomer}
        onDropProductOnQueueCustomer={handleProductDropOnQueue}
        autoDetectChat={autoDetectChat}
        handleUpdateAutoDetectChat={handleUpdateAutoDetectChat}
        autoDetectComment={autoDetectComment}
        handleUpdateAutoDetectComment={handleUpdateAutoDetectComment}
        refetchConversation={async () => {
          if (refetchConversation) await refetchConversation();
        }}
      />

      <SaleFloatingWidgets
        showingMessagingFloat={showingMessagingFloat}
        showingCommentsFloat={showingCommentsFloat}
        comments={comments}
        fetchCommentState={fetchCommentState}
        showingComment={showingComment}
        setShowingComment={setShowingComment}
        showingChatMessage={showingChatMessage}
        setShowingChatMessage={setShowingChatMessage}
        onLoadingMoreComment={loadMoreComment}
        isLoadingMoreComment={isPolling}
      />
      <FloatingOrderPanel
        open={floatingOpen}
        currentCustomer={floatingCustomer}
        initialData={floatingInitialData}
        onCancel={handleCloseFloating}
        onConfirm={handleConfirmFloatingOrder}
      />
    </>
  );
}
